'use client';
import React, { useState } from 'react';
import { Play, ExternalLink } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

interface YouTubePlayerProps {
  videoId: string;
  title: string;
  source: string;
  thumbnailSrc?: string;
  thumbnailAlt?: string;
  description?: string;
  className?: string;
}

export default function YouTubePlayer({
  videoId,
  title,
  source,
  thumbnailSrc,
  thumbnailAlt,
  description,
  className = '',
}: YouTubePlayerProps) {
  const [consent, setConsent] = useState<'pending' | 'accepted' | 'declined'>('pending');
  const [playerLoaded, setPlayerLoaded] = useState(false);

  const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`;

  const handleAccept = () => {
    setConsent('accepted');
    setPlayerLoaded(true);
  };

  const handleDecline = () => {
    setConsent('declined');
  };

  return (
    <div className={`rounded-xl overflow-hidden border border-[#E2E8EC] bg-[#F5F3EF] ${className}`}>
      {/* Thumbnail + consent state */}
      {!playerLoaded && (
        <div className="relative aspect-video bg-[#071923]/10 flex items-center justify-center overflow-hidden">
          {thumbnailSrc ? (
            <AppImage
              src={thumbnailSrc}
              alt={thumbnailAlt || `Miniature de la vidéo : ${title}`}
              width={640}
              height={360}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#071923] to-[#0A4C64] flex items-center justify-center">
              <Play size={48} className="text-white/30" aria-hidden="true" />
            </div>
          )}

          {/* Overlay */}
          <div className="absolute inset-0 bg-[#071923]/50 flex flex-col items-center justify-center gap-3 p-4">
            {consent === 'pending' && (
              <>
                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <Play size={24} className="text-[#071923] ml-1" aria-hidden="true" />
                </div>
                <p className="text-white text-sm font-semibold text-center max-w-xs">
                  {title}
                </p>
                <p className="text-white/70 text-xs text-center max-w-xs">
                  Source : {source}
                </p>
              </>
            )}
            {consent === 'declined' && (
              <div className="bg-white/95 rounded-xl p-4 max-w-sm text-center shadow-lg">
                <p className="text-[#071923] text-sm mb-3">
                  Cette vidéo est hébergée par YouTube. Vous pouvez autoriser son affichage ou l&apos;ouvrir directement sur YouTube.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <button
                    onClick={handleAccept}
                    className="px-4 py-2 bg-[#0A4C64] text-white text-sm font-semibold rounded-lg hover:bg-[#071923] transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D1A338]"
                  >
                    Autoriser et lire
                  </button>
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 border border-[#0A4C64] text-[#0A4C64] text-sm font-semibold rounded-lg hover:bg-[#0A4C64]/5 transition-colors min-h-[44px] flex items-center justify-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64]"
                  >
                    Ouvrir sur YouTube
                    <ExternalLink size={13} aria-hidden="true" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Consent prompt (below thumbnail when pending) */}
      {consent === 'pending' && !playerLoaded && (
        <div className="px-4 py-4 bg-[#F5F3EF] border-t border-[#E2E8EC]">
          <p className="text-xs text-[#536873] mb-3">
            Cette vidéo est hébergée par YouTube. Vous pouvez autoriser son affichage ou l&apos;ouvrir directement sur YouTube.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleAccept}
              className="px-4 py-2 bg-[#0A4C64] text-white text-sm font-semibold rounded-lg hover:bg-[#071923] transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D1A338]"
            >
              Autoriser et lire
            </button>
            <a
              href={youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 border border-[#0A4C64] text-[#0A4C64] text-sm font-semibold rounded-lg hover:bg-[#0A4C64]/5 transition-colors min-h-[44px] flex items-center justify-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64]"
            >
              Ouvrir sur YouTube
              <ExternalLink size={13} aria-hidden="true" />
            </a>
            <button
              onClick={handleDecline}
              className="px-4 py-2 text-[#536873] text-sm rounded-lg hover:text-[#071923] transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64]"
            >
              Refuser
            </button>
          </div>
        </div>
      )}

      {/* Loaded iframe */}
      {playerLoaded && (
        <div className="relative aspect-video">
          <iframe
            src={embedUrl}
            title={title}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 w-full h-full"
          />
        </div>
      )}

      {/* Meta info */}
      <div className="px-4 py-3 border-t border-[#E2E8EC]">
        <p className="text-xs font-semibold text-[#071923] mb-0.5">{title}</p>
        {description && <p className="text-xs text-[#536873] mb-1">{description}</p>}
        <p className="text-xs text-[#536873]">Source : {source}</p>
        {!playerLoaded && (
          <a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-[#0A4C64] hover:underline mt-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0A4C64] rounded"
          >
            Voir sur YouTube
            <ExternalLink size={11} aria-hidden="true" />
          </a>
        )}
      </div>
    </div>
  );
}
